export function isNumber(value: unknown, convert = false): value is number {
  if (typeof value === 'number') return !Number.isNaN(value);
  if (!convert) return false;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const converted = Number(trimmed);
    return trimmed.length > 0 && !Number.isNaN(converted);
  }

  return false;
}

export function toNumber(value: unknown, convert = false): number | undefined {
  if (typeof value === 'number') return Number.isNaN(value) ? undefined : value;
  if (!convert) return undefined;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const converted = Number(trimmed);
    return trimmed.length > 0 && !Number.isNaN(converted) ? converted : undefined;
  }

  return undefined;
}

/** Converts a value of unknown type to a number. An alias for toNumber() with convert=true. */
export const convertNumber = (value: unknown): number | undefined => toNumber(value, true);

// Move this? Kinda special cased.
export function normalizeNumberString(value: unknown, removeChars: string[]): string {
  // Escape special regex characters to prevent regex injection
  const escapedChars = removeChars.map((char) => char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = escapedChars.join('|');
  // All input is escaped using regex escape pattern to prevent injection
  // nosemgrep: javascript.lang.security.audit.detect-non-literal-regexp.detect-non-literal-regexp
  // eslint-disable-next-line security/detect-non-literal-regexp -- pattern is built from escaped characters for safe regex construction
  const regEx = new RegExp(pattern, 'gi');
  return value?.toString().toLocaleLowerCase().replace(regEx, '') ?? '';
}

/**
 * Parses a string into a number, with optional normalization.
 *
 * @param value - The string to parse into a number
 * @param normalize - Optional function to transform the string before parsing
 * @returns The parsed number, or undefined if parsing fails
 */
export function parseNumber(value?: string, normalize?: (value: unknown) => string): number | undefined {
  const normalized = normalize ? normalize(value) : value;
  return toNumber(normalized, true);
}

// Intentionally did not implement asNumber() function. If you want coercion just use the 'as' keyword.

export function add(a: number, b: number, decimalPlaces = 0) {
  const decimalShift = 10 ** decimalPlaces;
  return (a * decimalShift + b * decimalShift) / decimalShift;
}

export function subtract(a: number, b: number, decimalPlaces = 0) {
  const decimalShift = 10 ** decimalPlaces;
  return (a * decimalShift - b * decimalShift) / decimalShift;
}

export function round(value: number, decimalPlaces = 0) {
  const decimalShift = 10 ** decimalPlaces;
  return Math.round((value + Number.EPSILON) * decimalShift) / decimalShift;
}

export function hasDecimal(value: number): boolean {
  return value - Math.floor(value) !== 0;
}

export function isWhole(value: number): boolean {
  return !hasDecimal(value) && value >= 0;
}
